import { Notify } from "quasar";
import { computed, ref, watch } from "vue";

import { ErrorDialog } from "shared/boot/alert";
import { getLocaleText } from "shared/boot/i18n";
import {
  type ContactResourceType,
  type ContactType,
} from "shared/composables/useContact";
import { constantize } from "shared/helpers/string";
import { ContactDetail } from "shared/resources";
import OutreachContactService from "shared/services/api/OutreachContactService";

export function useOutreachContactDetails({
  contactId,
  contactType,
}: {
  contactId: number;
  contactType: ContactResourceType | "";
}) {
  const contactDetails = ref<ContactDetail>();
  const contact = ref<ContactType>();
  const loadingContactDetails = ref(false);
  const savingContactDetails = ref(false);
  const isContactDetailsDirty = ref(false);

  const contactDetailUpdatedBy = computed(
    () => contactDetails.value?.latestVersion?.user?.name
  );

  watch(
    contactDetails,
    (newValue, oldValue) => {
      if (!oldValue) return;
      isContactDetailsDirty.value = true;
    },
    { deep: true }
  );

  async function loadContactDetails() {
    try {
      loadingContactDetails.value = true;

      const response = await ContactDetail.includes([{ latestVersion: "user" }])
        .where({
          contactId,
          contactType: constantize(contactType),
        })
        .first();

      const defaultAddress = {
        addressLine1: "",
        addressLine2: "",
        city: "",
        country: "",
        postalCode: "",
      };

      if (!response.data) {
        contactDetails.value = new ContactDetail({
          address: null,
          contactId,
          contactType: constantize(contactType),
          defaultEmail: false,
          email: "",
          note: "",
          phone: "",
        });
      } else {
        contactDetails.value = response.data;
      }

      contactDetails.value.address = {
        ...defaultAddress,
        ...contactDetails.value.address,
      };
    } catch (error) {
      ErrorDialog(
        getLocaleText("outreach_contact_details.load_detail_error"),
        error
      );
    } finally {
      loadingContactDetails.value = false;
    }

    if (contactType) {
      try {
        [contact.value] = (
          await OutreachContactService.getAllContacts({
            filters: {
              document_ids: [`${contactType}_${contactId}`],
            },
          })
        ).data.contacts;
      } catch (error) {
        ErrorDialog(
          getLocaleText("outreach_contact_details.load_detail_error"),
          error
        );
      }
    }
  }

  async function saveContactDetails() {
    try {
      if (!contactDetails.value) return;

      savingContactDetails.value = true;

      if (!contactDetails.value.email) {
        contactDetails.value.defaultEmail = false;
      }

      await contactDetails.value.saveResource();
      await loadContactDetails();
      isContactDetailsDirty.value = false;

      Notify.create({
        actions: [{ color: "white", icon: "close" }],
        message: getLocaleText(
          "outreach_contact_details.save_detail_notification"
        ),
      });
    } catch (error) {
      ErrorDialog(
        getLocaleText("outreach_contact_details.save_detail_error"),
        error
      );
    } finally {
      savingContactDetails.value = false;
    }
  }

  return {
    contact,
    contactDetails,
    contactDetailUpdatedBy,
    isContactDetailsDirty,
    loadContactDetails,
    loadingContactDetails,
    saveContactDetails,
    savingContactDetails,
  };
}

export default useOutreachContactDetails;
